import { Component } from '@theme/component';

/** A scroll-snap gallery: the images work even when this module never loads. */
class FleiginSceneCarousel extends Component {
  #events;
  #resize;
  #frame = 0;
  #index = -1;
  #motion;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#events?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    if (this.refs.controls) this.refs.controls.hidden = true;
  }

  #initialize() {
    this.#cleanup();
    const { viewport, slides = [], previous, next, controls, dots = [] } = this.refs;
    this.#index = -1;
    if (!viewport || slides.length < 2 || !previous || !next || !controls) return;

    try {
      this.#events = new AbortController();
      const { signal } = this.#events;
      this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      viewport.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
      viewport.addEventListener('keydown', this.#onKeydown, { signal });
      previous.addEventListener('click', () => this.#goTo(this.#index - 1), { signal });
      next.addEventListener('click', () => this.#goTo(this.#index + 1), { signal });
      for (const dot of dots) {
        dot.addEventListener('click', () => this.#goTo(Number(dot.dataset.index)), { signal });
      }
      if ('ResizeObserver' in window) {
        this.#resize = new ResizeObserver(this.#scheduleUpdate);
        this.#resize.observe(viewport);
      }
      window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
      // Finish any smooth movement immediately when the shopper reduces motion.
      this.#motion.addEventListener('change', (event) => {
        if (event.matches) this.#goTo(Math.max(0, this.#index));
      }, { signal });
      this.#update();
      controls.hidden = false;
    } catch {
      this.#cleanup();
    }
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const { viewport, slides = [], previous, next, status, dots = [] } = this.refs;
    if (!viewport || !slides.length) return;
    const rtl = getComputedStyle(viewport).direction === 'rtl';
    const bounds = viewport.getBoundingClientRect();
    let closest = 0;
    let distance = Infinity;
    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const offset = Math.abs(rtl ? bounds.right - rect.right : rect.left - bounds.left);
      if (offset < distance) {
        closest = index;
        distance = offset;
      }
    });
    if (closest === this.#index) return;
    this.#index = closest;
    previous?.setAttribute('aria-disabled', String(closest === 0));
    next?.setAttribute('aria-disabled', String(closest === slides.length - 1));
    dots.forEach((dot, index) => {
      if (index === closest) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (status) {
      status.textContent = (this.dataset.statusTemplate || '__current__ / __total__')
        .replace('__current__', String(closest + 1))
        .replace('__total__', String(slides.length));
    }
  }

  #goTo(index) {
    const { viewport, slides = [] } = this.refs;
    if (!viewport || index < 0 || index >= slides.length || !Number.isInteger(index)) return;
    const bounds = viewport.getBoundingClientRect();
    const target = slides[index].getBoundingClientRect();
    const rtl = getComputedStyle(viewport).direction === 'rtl';
    // The physical delta also works with browsers' negative RTL scrollLeft values.
    const delta = rtl ? target.right - bounds.right : target.left - bounds.left;
    viewport.scrollTo({
      left: viewport.scrollLeft + delta,
      behavior: this.#motion?.matches ? 'auto' : 'smooth',
    });
    this.#scheduleUpdate();
  }

  #onKeydown = (event) => {
    if (event.target !== this.refs.viewport || event.altKey || event.ctrlKey || event.metaKey) return;
    const rtl = getComputedStyle(this.refs.viewport).direction === 'rtl';
    let target;
    if (event.key === 'ArrowRight') target = this.#index + (rtl ? -1 : 1);
    if (event.key === 'ArrowLeft') target = this.#index + (rtl ? 1 : -1);
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = (this.refs.slides || []).length - 1;
    if (target === undefined) return;
    event.preventDefault();
    this.#goTo(target);
  };
}

const REVEAL_MARKER = '[data-fl-reveal]:not([data-fl-reveal="off"])';
const UNSAFE_REGION = [
  '[data-fl-reveal="off"]', '[hidden]', '[inert]', 'dialog', '[role="dialog"]',
  'form', 'input', 'select', 'textarea', 'fieldset', 'button',
  'product-form-component', 'cart-items-component', 'cart-drawer-component',
  'quantity-selector-component', '.fh-hero', '.fh-stats-band',
].join(',');
const UNSAFE_CONTENT = [
  'form', 'input', 'select', 'textarea', 'fieldset', 'dialog', '[role="dialog"]',
  'button:not(.fl-scene__button):not(.fl-scene__dot)',
  'product-form-component', 'cart-items-component', 'cart-drawer-component',
].join(',');
const PROSE_SELECTOR = [
  '.fleigin-page__copy > *',
  '.rte > :is(p,h2,h3,h4,ul,ol,table,details,section)',
  '.shopify-policy__body > :is(p,h2,h3,h4,ul,ol,table,details,section)',
].join(',');
const MODULE_SELECTOR = [
  '.fleigin-page__heading', '.shopify-policy__title',
  ':scope > .shopify-section > section', ':scope > section',
  ':scope > .shopify-section > .section', 'h1',
].join(',');

/**
 * A lifecycle host for main-content enhancement, including Shopify section renders.
 * Author data-fl-reveal on the outer module; data-fl-reveal="off" opts out a subtree.
 * Neither author markers nor script loading alone can hide any content.
 */
class FleiginRevealManager extends Component {
  #scope;
  #events;
  #observer;
  #mutations;
  #motion;
  #pending = new Set();
  #seen = new WeakSet();
  #frame = 0;
  #scanFrame = 0;
  #failed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleScan();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#events?.abort();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#scanFrame);
    this.#frame = 0;
    this.#scanFrame = 0;
    this.#showAll();
  }

  #initialize() {
    this.#cleanup();
    this.#scope = this.closest('main, #MainContent');
    this.#failed = false;
    if (!this.#scope || !('IntersectionObserver' in window)) return;
    try {
      this.#events = new AbortController();
      const { signal } = this.#events;
      this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#observer = new IntersectionObserver((entries) => {
        try {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#show(entry.target);
          }
        } catch {
          this.#failOpen();
        }
      }, { root: null, rootMargin: '0px 0px 40px 0px', threshold: 0.01 });
      this.#scope.addEventListener('focusin', (event) => {
        const module = event.target.closest?.('.fl-reveal-pending');
        if (module) this.#show(module, true);
      }, { signal });
      this.#motion.addEventListener('change', () => {
        if (this.#motion.matches) this.#showAll();
        else this.#scheduleScan();
      }, { signal });
      // Capture also receives .page-wrapper scrolls on the desktop theme layout.
      document.addEventListener('scroll', this.#scheduleCheck, { capture: true, passive: true, signal });
      window.addEventListener('resize', this.#scheduleCheck, { passive: true, signal });
      window.addEventListener('beforeprint', this.#showAll, { signal });
      window.addEventListener('afterprint', this.#showAll, { signal });
      document.addEventListener('shopify:section:load', this.#scheduleScan, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        for (const module of this.#pending) {
          if (event.target.contains(module)) this.#show(module, true);
        }
      }, { signal });
      this.#mutations = new MutationObserver(this.#scheduleScan);
      this.#mutations.observe(this.#scope, { childList: true, subtree: true });
      this.#scan();
    } catch {
      this.#failOpen();
    }
  }

  #failOpen() {
    this.#failed = true;
    this.#cleanup();
  }

  #show(module, immediately = false) {
    if (immediately) module.classList.remove('fl-reveal-ready');
    module.classList.remove('fl-reveal-pending');
    this.#pending.delete(module);
    this.#observer?.unobserve(module);
  }

  #showAll = () => {
    for (const module of this.#pending) {
      module.classList.remove('fl-reveal-pending', 'fl-reveal-ready');
    }
    this.#pending.clear();
    this.#observer?.disconnect();
  };

  #scheduleScan = () => {
    if (this.#failed || this.#scanFrame) return;
    this.#scanFrame = requestAnimationFrame(() => {
      this.#scanFrame = 0;
      this.#scan();
    });
  };

  #scheduleCheck = () => {
    if (this.#failed || this.#frame || !this.#pending.size) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      try {
        const height = document.documentElement.clientHeight || window.innerHeight;
        for (const module of this.#pending) {
          if (!module.isConnected || module.getBoundingClientRect().top < height + 40) this.#show(module);
        }
      } catch {
        this.#failOpen();
      }
    });
  };

  #scan() {
    if (!this.#scope || this.#failed || this.#motion?.matches) return;
    try {
      // Explicit modules win. Prose children win over large generic section wrappers.
      const selected = [];
      for (const selector of [REVEAL_MARKER, PROSE_SELECTOR, MODULE_SELECTOR]) {
        for (const module of this.#scope.querySelectorAll(selector)) {
          if (selected.some((other) => other === module || other.contains(module) || module.contains(other))) continue;
          if (module.closest(UNSAFE_REGION) || module.querySelector(UNSAFE_CONTENT)) continue;
          if (module.parentElement?.closest('details:not([open])')) continue;
          selected.push(module);
        }
      }
      const height = document.documentElement.clientHeight || window.innerHeight;
      for (const module of selected) {
        if (this.#seen.has(module)) continue;
        this.#seen.add(module);
        const rect = module.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0 || rect.height > height * 1.5) continue;
        // Visible, previously passed and keyboard-focused content never gets hidden.
        if (rect.top <= height + 40 || module.contains(document.activeElement)) continue;
        // Observe first. If the API throws, the element has never been hidden.
        this.#observer.observe(module);
        this.#pending.add(module);
        module.classList.add('fl-reveal-ready', 'fl-reveal-pending');
      }
      for (const module of this.#pending) {
        if (!module.isConnected) this.#show(module, true);
      }
    } catch {
      this.#failOpen();
    }
  }
}

if (!customElements.get('fleigin-scene-carousel')) {
  customElements.define('fleigin-scene-carousel', FleiginSceneCarousel);
}
if (!customElements.get('fleigin-reveal-manager')) {
  customElements.define('fleigin-reveal-manager', FleiginRevealManager);
}

function initializeReveals() {
  const main = document.getElementById('MainContent') || document.querySelector('main');
  if (!main || main.querySelector('fleigin-reveal-manager')) return;
  main.append(document.createElement('fleigin-reveal-manager'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeReveals, { once: true });
} else {
  initializeReveals();
}
